import { useMemo } from 'react';
import { Clock, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGA4HourlyActivity } from '@/hooks/useGA4ActivityData';
import { useAuthStore } from '@/stores/auth-store';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * Audience Rhythm View - GitHub-style contribution heatmap
 * Shows when your users are most active (Day vs Hour)
 */
export function AudienceRhythmView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGA4 = !!activeProperty?.ga4Property;

  const { data: activityData, isLoading } = useGA4HourlyActivity();

  // Generate heatmap data
  const heatmapData = useMemo(() => {
    if (!activityData) return null;

    // Create a 7x24 grid (days x hours)
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let maxValue = 0;

    // Fill grid with activity data
    for (const entry of activityData) {
      const dayIndex = entry.dayOfWeek; // 0 = Sunday in GA4, we'll adjust
      const hour = entry.hour;
      const value = entry.activeUsers;

      // Adjust day index (GA4: 0=Sun, we want 0=Mon)
      const adjustedDay = dayIndex === 0 ? 6 : dayIndex - 1;

      if (adjustedDay >= 0 && adjustedDay < 7 && hour >= 0 && hour < 24) {
        grid[adjustedDay][hour] = value;
        maxValue = Math.max(maxValue, value);
      }
    }

    return { grid, maxValue };
  }, [activityData]);

  // Find peak activity
  const peakActivity = useMemo(() => {
    if (!heatmapData) return null;

    let peakDay = 0;
    let peakHour = 0;
    let peakValue = 0;

    heatmapData.grid.forEach((dayData, dayIndex) => {
      dayData.forEach((value, hour) => {
        if (value > peakValue) {
          peakValue = value;
          peakDay = dayIndex;
          peakHour = hour;
        }
      });
    });

    return { day: DAYS[peakDay], hour: peakHour, value: peakValue };
  }, [heatmapData]);

  // Calculate intensity level (0-4) for color
  const getIntensityLevel = (value: number, maxValue: number): number => {
    if (maxValue === 0 || value === 0) return 0;
    const ratio = value / maxValue;
    if (ratio < 0.25) return 1;
    if (ratio < 0.5) return 2;
    if (ratio < 0.75) return 3;
    return 4;
  };

  const intensityColors = [
    'bg-muted/30', // 0 - empty
    'bg-blue-900/50', // 1 - low
    'bg-blue-700/70', // 2 - medium-low
    'bg-blue-500/80', // 3 - medium-high
    'bg-blue-400', // 4 - high
  ];

  if (!hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Connect GA4 to see audience activity</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Clock className="h-6 w-6 text-primary" />
            Audience Rhythm
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            When your users are most active
          </p>
        </div>

        {/* Peak Activity Badge */}
        {peakActivity && (
          <div className="glass-panel px-4 py-2">
            <p className="text-xs text-muted-foreground">Peak Activity</p>
            <p className="text-lg font-bold text-primary">
              {peakActivity.day} at {peakActivity.hour}:00
            </p>
          </div>
        )}
      </div>

      {/* Heatmap */}
      <div className="glass-panel p-6 flex-1">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground animate-pulse">
            Loading activity data...
          </div>
        ) : !heatmapData ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No activity data available</p>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Hour labels */}
            <div className="flex ml-12 mb-2">
              {HOURS.filter((h) => h % 2 === 0).map((hour) => (
                <div
                  key={hour}
                  className="text-xs text-muted-foreground"
                  style={{ width: '32px', marginLeft: hour === 0 ? 0 : '32px' }}
                >
                  {hour}h
                </div>
              ))}
            </div>

            {/* Grid */}
            <div className="flex-1 flex flex-col gap-1">
              {DAYS.map((day, dayIndex) => (
                <div key={day} className="flex items-center gap-2">
                  {/* Day label */}
                  <div className="w-10 text-sm text-muted-foreground text-right">
                    {day}
                  </div>

                  {/* Hour cells */}
                  <div className="flex gap-0.5 flex-1">
                    {HOURS.map((hour) => {
                      const value = heatmapData.grid[dayIndex][hour];
                      const intensity = getIntensityLevel(value, heatmapData.maxValue);

                      return (
                        <div
                          key={hour}
                          className={cn(
                            'flex-1 aspect-square rounded-sm transition-all hover:ring-2 hover:ring-primary/50 cursor-pointer',
                            intensityColors[intensity]
                          )}
                          title={`${day} ${hour}:00 - ${value} active users`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-border">
              <span className="text-xs text-muted-foreground">Less</span>
              {intensityColors.map((color, i) => (
                <div
                  key={i}
                  className={cn('w-4 h-4 rounded-sm', color)}
                />
              ))}
              <span className="text-xs text-muted-foreground">More</span>
            </div>
          </div>
        )}
      </div>

      {/* Insights */}
      {peakActivity && (
        <div className="grid grid-cols-3 gap-4">
          <InsightCard
            title="Best Day"
            value={peakActivity.day}
            description="Highest user activity"
            color="blue"
          />
          <InsightCard
            title="Peak Hour"
            value={`${peakActivity.hour}:00`}
            description="Most active time"
            color="emerald"
          />
          <InsightCard
            title="Peak Users"
            value={peakActivity.value.toLocaleString()}
            description="At peak time"
            color="purple"
          />
        </div>
      )}
    </div>
  );
}

interface InsightCardProps {
  title: string;
  value: string;
  description: string;
  color: 'blue' | 'emerald' | 'purple';
}

function InsightCard({ title, value, description, color }: InsightCardProps) {
  const colorClasses = {
    blue: 'text-blue-400',
    emerald: 'text-emerald-400',
    purple: 'text-purple-400',
  };

  return (
    <div className="glass-panel p-4">
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className={cn('text-2xl font-bold mt-1', colorClasses[color])}>{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  );
}
